import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Search, MailWarning } from "lucide-react";
import {
  Pill, EmptyState, Loading, ErrorState, TableShell, Th, Pagination, Card,
} from "./AdminUI";

const OUTCOME_TONE = { sent: "green", mocked: "amber", failed: "red" };

export default function EmailTab() {
  const [type, setType] = useState("");
  const [outcome, setOutcome] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null);
    const params = { page, page_size: 25 };
    if (type) params.email_type = type;
    if (outcome) params.outcome = outcome;
    if (q.trim()) params.q = q.trim();
    api.get("/admin/email-log", { params })
      .then(r => setD(r.data)).catch(e => setError(e.response?.data?.detail || e.message));
  }, [type, outcome, q, page]);

  useEffect(() => { setPage(1); }, [type, outcome]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!d) return <Loading />;

  return (
    <div className="space-y-4" data-testid="admin-email-tab">
      {d.delivery.banner && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-start gap-3"
          data-testid="email-mocked-banner">
          <MailWarning size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">{d.delivery.banner}</p>
            <p className="text-xs text-[#222]/60 mt-0.5">
              Every attempt below was written to the log instead of being sent. Add a Resend API key and
              verify the sender domain to switch delivery on — no code change is needed.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {["sent", "mocked", "failed"].map(k => (
          <button key={k} onClick={() => setOutcome(outcome === k ? "" : k)}
            className={`bg-white rounded-2xl p-4 border text-left ${outcome === k ? "border-[#2E6B57]" : "border-black/5"}`}
            data-testid={`email-outcome-${k}`}>
            <p className="text-[10px] uppercase tracking-wider text-[#222]/45 font-semibold">{k}</p>
            <p className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{d.by_outcome[k] || 0}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 bg-white border border-black/10 rounded-full px-3 py-2 flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="text-[#222]/40" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Recipient email…"
            className="flex-1 outline-none text-sm bg-transparent min-w-0" data-testid="email-search" />
        </div>
        <select value={type} onChange={e => setType(e.target.value)}
          className="px-3 py-2 rounded-full text-xs font-semibold bg-white border border-black/10 outline-none"
          data-testid="email-type-filter">
          <option value="">All email types</option>
          {d.types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {d.items.length === 0 ? (
        <EmptyState title="No delivery attempts recorded"
          body="Nothing has been emailed yet with the current filters. Attempts appear here as soon as LIMYA tries to send."
          testid="email-empty" />
      ) : (
        <>
          <TableShell testid="email-table">
            <thead>
              <tr>
                <Th>Type</Th><Th>Recipient</Th>
                <Th className="hidden md:table-cell">Subject</Th>
                <Th>Outcome</Th><Th className="hidden lg:table-cell">Attempted</Th>
              </tr>
            </thead>
            <tbody>
              {d.items.map(r => (
                <tr key={r.id} className="border-t border-black/5" data-testid={`email-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs font-medium whitespace-nowrap">{r.email_type}</td>
                  <td className="px-4 py-3 text-xs truncate max-w-[180px]">{r.recipient}</td>
                  <td className="px-4 py-3 text-xs text-[#222]/60 hidden md:table-cell truncate max-w-[260px]">{r.subject}</td>
                  <td className="px-4 py-3">
                    <Pill tone={OUTCOME_TONE[r.outcome] || "neutral"}>{r.outcome}</Pill>
                    {r.failure_reason && <p className="text-[10px] text-red-600 mt-1 max-w-[200px] truncate">{r.failure_reason}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#222]/50 hidden lg:table-cell whitespace-nowrap">
                    {r.attempted_at ? new Date(r.attempted_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableShell>
          <Pagination page={d.page} pageSize={d.page_size} total={d.total} onPage={setPage} testid="email-pagination" />
        </>
      )}

      <Card title="What is and isn't stored" testid="email-privacy-note">
        <p className="text-xs text-[#222]/60">
          Only delivery metadata is kept: type, recipient, subject, timestamp, outcome, provider message id
          and failure reason. Email bodies, password-reset links and verification tokens are never persisted.
        </p>
        <p className="text-xs text-[#222]/60 mt-2">{d.preferences_note}</p>
      </Card>
    </div>
  );
}
